import { User, Workorder } from '@certhon/models';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { TECHNICIAN_COLORS } from '../../shared';

interface TechnicianSelectorProps {
  workordersByTech: Map<number, Workorder[]>;
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  userMap: Map<number, User>;
}

interface TechnicianTab {
  userId: number;
  name: string;
  count: number;
  color: string;
}

const TAB_WIDTH = 140;
const ALL_TAB_WIDTH = 60;
const TAB_GAP = 4;
const DROPDOWN_BUTTON_WIDTH = 80;

export function TechnicianSelector({
  workordersByTech,
  selectedId,
  onSelect,
  userMap,
}: TechnicianSelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);

  // Calculate total workorders across all technicians
  const totalCount = Array.from(workordersByTech.values()).reduce(
    (sum, wos) => sum + wos.length,
    0,
  );

  // Sort technicians by name and assign colors
  const sortedTechnicians = Array.from(workordersByTech.entries())
    .map(([userId, workorders]) => {
      const user = userMap.get(userId);
      const displayName = user?.name || user?.loginname || `User ${userId}`;
      return {
        userId,
        name: displayName,
        count: workorders.length,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Create color mapping based on sorted order (same logic as in dayplanning.panel.tsx)
  const technicianColors = new Map<number, string>();
  const techIds = Array.from(workordersByTech.keys()).sort();
  techIds.forEach((id, idx) => {
    technicianColors.set(id, TECHNICIAN_COLORS[idx % TECHNICIAN_COLORS.length]);
  });

  // Create typed technician tabs with colors
  const technicianTabs: TechnicianTab[] = sortedTechnicians.map(
    ({ userId, name, count }) => ({
      userId,
      name,
      count,
      color: technicianColors.get(userId) || TECHNICIAN_COLORS[0],
    }),
  );

  // Observe parent element width (which has the actual constrained width)
  useEffect(() => {
    const parent = containerRef.current?.parentElement;
    if (!parent) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setAvailableWidth(entry.contentRect.width);
      }
    });

    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  // Calculate visible and overflow tabs
  const calculateVisibleTabs = () => {
    // If no width yet, show no technician tabs (will adjust on first render)
    if (availableWidth <= 0) {
      return { visible: [], overflow: technicianTabs };
    }

    // Calculate space for technician tabs (subtract "All" tab and dropdown button)
    const spaceForTechTabs =
      availableWidth - ALL_TAB_WIDTH - DROPDOWN_BUTTON_WIDTH;

    // Calculate how many technician tabs can fit (include gap between tabs)
    const maxTechTabs = Math.max(
      0,
      Math.floor(spaceForTechTabs / (TAB_WIDTH + TAB_GAP)),
    );

    if (maxTechTabs >= technicianTabs.length) {
      // All tabs fit, no overflow needed
      return { visible: technicianTabs, overflow: [] };
    }

    // Determine which tabs to show
    const selectedTab = technicianTabs.find((t) => t.userId === selectedId);
    const selectedIndex = selectedTab
      ? technicianTabs.indexOf(selectedTab)
      : -1;

    let visible: TechnicianTab[];
    let overflow: TechnicianTab[];

    if (selectedIndex !== -1 && selectedIndex >= maxTechTabs) {
      // Selected tab is in overflow - swap it into visible area
      // Show first (maxTechTabs - 1) tabs + selected tab
      visible = [
        ...technicianTabs.slice(0, maxTechTabs - 1),
        technicianTabs[selectedIndex],
      ];
      overflow = [
        ...technicianTabs.slice(maxTechTabs - 1, selectedIndex),
        ...technicianTabs.slice(selectedIndex + 1),
      ];
    } else {
      // Normal case: show first N tabs
      visible = technicianTabs.slice(0, maxTechTabs);
      overflow = technicianTabs.slice(maxTechTabs);
    }

    return { visible, overflow };
  };

  const { visible: visibleTabs, overflow: overflowTabs } =
    calculateVisibleTabs();

  const renderTabContent = (tab: TechnicianTab) => (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
        style={{ backgroundColor: tab.color }}
      />
      <span className="truncate">{tab.name}</span>
      <span className="flex-shrink-0">({tab.count})</span>
    </div>
  );

  return (
    <div ref={containerRef}>
      <Tabs
        value={selectedId === null ? 'all' : String(selectedId)}
        onValueChange={(value) =>
          onSelect(value === 'all' ? null : Number(value))
        }
      >
        <TabsList className="h-auto w-full justify-start overflow-hidden">
          <TabsTrigger value="all" className="w-[60px] flex-none">
            All ({totalCount})
          </TabsTrigger>
          {visibleTabs.map((tab) => (
            <TabsTrigger
              key={tab.userId}
              value={String(tab.userId)}
              className="min-w-[140px] flex-1"
            >
              {renderTabContent(tab)}
            </TabsTrigger>
          ))}
          {overflowTabs.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="min-w-[80px] flex-shrink-0 gap-1"
                >
                  {overflowTabs.length} more
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                {overflowTabs.map((tab) => (
                  <DropdownMenuItem
                    key={tab.userId}
                    onClick={() => onSelect(tab.userId)}
                  >
                    {renderTabContent(tab)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TabsList>
      </Tabs>
    </div>
  );
}
